import { Button } from '@/components/ui/Button';
import { RESOURCES_STRINGS } from '../constants/resources-strings';
import { resourceCoverUrl } from '../resource-cover';
import { RESOURCE_ACTION_LABEL, resolveResourceAction } from '../resource-action';
import type { ResourceCardProps } from '../types';
import { ResourcePartnerCredit } from './ResourcePartnerCredit';

/** 4-colour accent palette, cycled by accentIndex % 4. */
const ACCENT_COLORS: readonly string[] = ['#2B958E', '#FED13D', '#EF476F', '#00405C'];

/**
 * Formats an ISO date string to "Month Year" (e.g. "September 2026").
 */
function formatAddedDate(isoDate: string): string {
  try {
    return new Date(isoDate).toLocaleDateString('en-GB', {
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

export interface ResourceCardComponentProps extends ResourceCardProps {
  /** Called when the user clicks "Open Resource". Caller handles URL/download logic. */
  onOpen: () => void;
  /**
   * Put the cover photo across the top of the card, 16:9, like the events
   * cards, instead of the small thumbnail beside the text (dashboard only).
   */
  coverOnTop?: boolean;
}

/**
 * One card in the "All Resources" grid.
 *
 * Each card has a 4px top border in a cycling accent colour (green → amber →
 * rose), rounded top corners suppressed to abut the top border cleanly, a
 * separator, and a footer row with "Added [month year]" and "Open Resource →".
 *
 * The cover thumbnail sits beside the title and description, matching
 * FolderCard, and falls back to a placeholder photo when the item has no
 * cover image of its own.
 *
 * The footer action reads "Open Resource" for external links and
 * "View Details" for file-backed items, resolved through
 * `resolveResourceAction` so the label and the click handler always agree.
 */
export function ResourceCard({
  title,
  description,
  createdAt,
  coverImageUrl,
  externalUrl,
  fileUrl,
  accentIndex,
  trustedPartner,
  onOpen,
  coverOnTop = false,
}: ResourceCardComponentProps) {
  const accentColor = ACCENT_COLORS[accentIndex % ACCENT_COLORS.length];
  const addedDate = formatAddedDate(createdAt);
  const actionLabel = RESOURCE_ACTION_LABEL[resolveResourceAction({ externalUrl, fileUrl })];
  const coverUrl = resourceCoverUrl(coverImageUrl);
  const cover = (
    <div
      className={
        coverOnTop
          ? 'aspect-video w-full shrink-0 overflow-hidden bg-[#F7F9FB]'
          : 'aspect-[2/1] w-1/3 shrink-0 overflow-hidden rounded-md bg-[#F7F9FB]'
      }
    >
      {coverUrl && <img src={coverUrl} alt="" className="h-full w-full object-cover" />}
    </div>
  );

  return (
    <article className="flex flex-col overflow-hidden rounded-xl border border-card-border bg-white shadow-card transition-shadow hover:shadow-lg">
      {/* Accent bar — overflow-hidden clips it to the card's border-radius so it curves with the corners */}
      <div className="h-1 shrink-0" style={{ backgroundColor: accentColor }} />
      {coverOnTop && cover}
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-4 flex flex-1 items-start gap-4">
          <div className="flex min-w-0 flex-1 flex-col">
            {/* Title */}
            {/* The title opens the resource too, same as the footer action (UX
                sheet, StartupAdminPatty r39). */}
            <h3 className="mb-2 text-[15px] font-semibold leading-[1.4] text-[#001928] sm:text-[15px]">
              <Button
                variant="link"
                onClick={onOpen}
                className="text-left text-[15px]! leading-[1.4]! text-[#001928]! hover:text-green! hover:underline"
              >
                {title}
              </Button>
            </h3>

            {/* Description */}
            <p className="line-clamp-3 text-[13px] leading-[1.5] text-[#A0AEBC]">{description}</p>
          </div>

          {/* Cover thumbnail — trailing third of the card body, same as FolderCard */}
          {!coverOnTop && cover}
        </div>

        {/* Partner credit (FR-278): above the separator so it reads as part
            of the resource rather than of the footer metadata. Absent
            entirely when nobody is credited. */}
        {trustedPartner && (
          <div className="mb-4">
            <ResourcePartnerCredit partner={trustedPartner} />
          </div>
        )}

        {/* Separator */}
        <hr className="mb-4 border-[#F7F9FB]" style={{ borderTopWidth: '2px' }} />

        {/* Footer */}
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-[#A0AEBC]">
            {RESOURCES_STRINGS.ADDED_PREFIX} {addedDate}
          </span>
          <button
            type="button"
            onClick={onOpen}
            className="cursor-pointer text-[12px] font-semibold text-[#4DBA72] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4DBA72]"
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </article>
  );
}
